import {
  call, put, select, takeLatest,
} from 'redux-saga/effects';
import { push } from 'react-router-redux';

import GuestApi from 'apis/supremeGolfApi/GuestApi';
import TimeSlotsApi from 'apis/supremeGolfApi/TimeSlotsApi';
import GuestActions, { GuestTypes } from 'reducers/guest';
import CheckoutActions from 'reducers/checkout';
import FlowActions from 'reducers/flow';
import TrackingActions from 'reducers/tracking';
import TimeSlotsActions from 'reducers/timeSlots';
import {
  retryPrepare,
  teeTimeFailure,
  trackBookingSuccessEvent,
  trackBookingFailureEvent,
  prepareReservationData,
} from 'sagas/checkout';

import {
  BOOKING_CONFIRMATION, CHECKOUT, SIGN_IN, TEE_TIME_ERROR,
} from 'utils/routes';
import { GUEST_BLOCKED_MESSAGE, INTERNAL_CHECKOUT_ERROR_MESSAGES, UNAVAILABLE_TEE_TIMES_ERROR_CODE } from 'utils/constants';
import { matchRoute } from 'utils/routeHelpers';
import { getCommonTrackingParams } from './tracking';

export function* trackGuestEvent({ event, teeTime, otherParams = {} }) {
  const course = yield select((state) => state.courseDetail.course);

  const trackingParams = yield getCommonTrackingParams({
    course,
    teeTime,
    otherParams,
  });

  yield put(TrackingActions.trackGAEvent(event, trackingParams));
}

export function* trackGuestCheckoutEvent({ name, screenName, otherParams }) {
  const { guestInfo } = yield select((state) => state.guest);
  let preparedTeeTime = yield select((state) => state.checkout.preparedTeeTime);
  const extraParams = { ...otherParams } || {};
  if (extraParams?.preparedTeeTime) {
    preparedTeeTime = extraParams.preparedTeeTime;
    delete extraParams.preparedTeeTime;
  }

  const params = {
    teeTime: {
      ...preparedTeeTime.teeTime,
      teeTimeId: preparedTeeTime?.teeTimeId,
      players: preparedTeeTime?.qty,
    },
    other: {
      ...guestInfo,
      screenName,
      signInMode: 'guest',
      ...extraParams,
    },
  };

  yield put(GuestActions.trackGuestEvent({
    name,
    label: name.replaceAll('_', ' '),
    category: `${screenName} event`,
  }, params.teeTime, params.other));
}

export function* trackGuestAbandon({ params }) {
  const { location } = yield select((state) => state.router);
  const excludedRoutes = [CHECKOUT, BOOKING_CONFIRMATION, SIGN_IN];

  if (excludedRoutes.every((item) => !matchRoute(item, location.pathname))) {
    yield* trackGuestCheckoutEvent({
      name: 'guest_checkout_Abandoned',
      screenName: 'Guest checkout',
      otherParams: params,
    });
  }
}

export function* requestValidateGuestEmail({ email, cb }) {
  try {
    yield call(GuestApi.validateEmail, { email });
    yield put(GuestActions.validateGuestEmailSuccess());
    cb();
  } catch (error) {
    const errorMessage = error?.response?.data?.error ?? error?.message;
    yield put(GuestActions.validateGuestEmailError(errorMessage));
    if (cb) cb(errorMessage);
  }
}

export function* prepareGuestTeeTime(teeTimeId, qty) {
  const data = yield call(TimeSlotsApi.prepare, { teeTimeId, qty });
  if (data?.error) {
    const { error, statusCode, offerUrl } = data || {};
    yield put(TimeSlotsActions.prepareTeeTimeError(error, statusCode, offerUrl));
    if (data?.failureSteps) yield put(CheckoutActions.setFailureSteps(data.failureSteps));
    if (!offerUrl) yield* teeTimeFailure(statusCode, { errorMessage: error });
    return true;
  }

  yield put(TimeSlotsActions.prepareTeeTimeDone(data.preparedTeeTime));
  return false;
}

export function* createGuestCreditCard({ token, gRecaptchaResponseData }) {
  try {
    const data = yield call(GuestApi.addGuestCreditCard, token, gRecaptchaResponseData);
    const { creditCard } = data;
    yield put(GuestActions.createGuestCreditCardDone(creditCard));
  } catch (error) {
    const errorMessage = error?.response?.data?.message ?? error?.message;
    yield put(GuestActions.createGuestCreditCardError(errorMessage));
  }
}

export function* makeGuestPayment({ paymentType, paymentData, cb }) {
  try {
    const authToken = yield select(({ flow }) => flow?.checkout?.authToken);
    const creditCard = yield select(({ checkout }) => checkout.creditCard);

    const data = yield call(GuestApi.makeGuestPayment, {
      paymentType,
      paymentData: { ...paymentData, creditCardAccessToken: creditCard?.accessToken },
      authToken,
    });

    if (cb) cb({ success: true });
    const { accessToken, offer, reservationId } = data.receipt || {};
    const { teeTimeReservationId } = offer;

    const transactionParams = prepareReservationData(offer);
    yield* trackGuestCheckoutEvent({
      name: 'guest_booking_success',
      screenName: 'Guest checkout',
      otherParams: {
        ...transactionParams,
        reservationId,
        eventLabel: `Guest booking - ${teeTimeReservationId}`,
        userType: 'guest',
      },
    });

    yield* trackBookingSuccessEvent(offer, { reservationId, userType: 'guest' });

    yield put(GuestActions.makeGuestPaymentDone(data.receipt));
    yield put(CheckoutActions.resetCheckout());
    yield put(FlowActions.resetFlow());

    const { query } = yield select((state) => state.router.location);

    const guestQs = `${query?.isAppClipUrl === 'true' ? '&webView=true&isAppClipUrl=true' : ''}${query?.isAnchoredTeeTime === 'true' ? '&isAnchoredTeeTime=true' : ''}`;
    yield put(push(`${BOOKING_CONFIRMATION.replace(':reservationId', teeTimeReservationId)}?accessToken=${accessToken}${guestQs}`));
  } catch (error) {
    const {
      response: {
        data: {
          error: explanatoryError,
          isReservationUnconfirmed: isRecoverable,
          reservationUrl,
          reservationId,
          isPrepaidOnProvider,
          failureSteps,
        },
      },
    } = error || {};
    if (cb) cb({ success: false });
    yield* trackGuestCheckoutEvent({
      name: 'guest_booking_failed',
      screenName: 'Guest checkout',
    });

    yield* trackBookingFailureEvent(reservationId, {
      reservationId,
      userType: 'guest',
    });

    const errorMessage = explanatoryError || error.message;
    yield put(GuestActions.makeGuestPaymentError(errorMessage));
    yield put(CheckoutActions.setFailureSteps(failureSteps));

    if (failureSteps?.retryTeeTime) {
      yield* retryPrepare();
      return;
    }

    if (!isRecoverable && isPrepaidOnProvider) {
      yield* teeTimeFailure(TEE_TIME_ERROR, { reservationUrl, isGuest: true });
    } else if (!isRecoverable && INTERNAL_CHECKOUT_ERROR_MESSAGES.includes(errorMessage)) {
      yield* teeTimeFailure(UNAVAILABLE_TEE_TIMES_ERROR_CODE, { errorMessage, isGuest: true });
    } else if (!isRecoverable && errorMessage !== GUEST_BLOCKED_MESSAGE) {
      yield* teeTimeFailure(UNAVAILABLE_TEE_TIMES_ERROR_CODE, { isGuest: true });
    }
  }
}

function* requestValidateGuestEmailWatcher() {
  yield takeLatest(GuestTypes.VALIDATE_GUEST_EMAIL, requestValidateGuestEmail);
}

function* createGuestCreditCardWatcher() {
  yield takeLatest(GuestTypes.CREATE_GUEST_CREDIT_CARD, createGuestCreditCard);
}

function* makeGuestPaymentWatcher() {
  yield takeLatest(GuestTypes.MAKE_GUEST_PAYMENT, makeGuestPayment);
}

function* trackGuestEventWatcher() {
  yield takeLatest(GuestTypes.TRACK_GUEST_EVENT, trackGuestEvent);
}

function* trackGuestCheckoutEventWatcher() {
  yield takeLatest(GuestTypes.TRACK_GUEST_CHECKOUT_EVENT, trackGuestCheckoutEvent);
}

function* trackGuestAbandonWatcher() {
  yield takeLatest(GuestTypes.TRACK_GUEST_ABANDON, trackGuestAbandon);
}

export default [
  requestValidateGuestEmailWatcher,
  createGuestCreditCardWatcher,
  makeGuestPaymentWatcher,
  trackGuestEventWatcher,
  trackGuestAbandonWatcher,
  trackGuestCheckoutEventWatcher,
];
